// ExecutiveReport.js — client-side PDF executive report for SecureShield365.
//
// Exports:
//   ReportDocument        — @react-pdf/renderer <Document> for the report
//   DownloadReportButton  — pill-styled trigger; renders the PDF on CLICK
//                           (imperative pdf().toBlob()), never on mount
//
// Every figure traces to live `report` / `scans` / `report.scored_settings`.
// No fabricated numbers, no placeholder/TBD sections.
//
// Graceful degradation:
//   - When report or scans are missing, the button refuses to render an
//     active link.
//   - When report.scored_settings is absent (older cached report) the
//     severity / coverage / regression / findings sections quietly
//     disappear; the rest of the report still renders.
//   - Logo Images are HEAD-checked before being passed to react-pdf so a
//     missing /patriot-logo.png or /assets/logos/{slug}.png can never crash
//     the PDF (it falls back to bold text).

import React from "react";
import {
  Document, Page, View, Text, StyleSheet,
  Svg, Path, Line, Rect, G,
  Image, pdf,
} from "@react-pdf/renderer";
import { sevKey, sevPalette, compareFindings } from "./severity";
import { getTier, TIER_NAMES_ASC } from "./tiers";
import { CREDIT_OVERRIDES, OVERRIDE_SLICE_COLORS, COMPOSITION_LABELS, notAssessedOverriddenLine } from "./composition";
import { tileDisposition, verdictOf, TILE_ADDRESSED, TILE_EXCLUDED } from "./overrideStatus";
import { baselineIsSecureScan365, exactScoringBaseline } from "./regressionProvenance";
import { fmtInstant } from "../utils/dates";

// ── Theme — mirrors T in SecureShield365.js so the PDF reads as the same
//    product. Dark navy reserved for the cover band.
const C = {
  bg: "#0b1f3a",
  panel: "#112b4d",
  border: "rgba(255,255,255,0.12)",
  borderSoft: "rgba(255,255,255,0.08)",
  textLight: "#edf4ff",
  textDark: "#0b1f3a",
  mutedLight: "#a8bddc",
  mutedDark: "#5b7390",
  paper: "#ffffff",
  paperAlt: "#f4f6fa",
  blue: "#44a6ff",
  green: "#53c46d",
  yellow: "#f3b53f",
  orange: "#ff7a45",
  red: "#e14d57",
  critical: "#b3122a",
};

// ── Maturity tiers come from ./tiers (canonical, mirrors getLevel() in
//    SecureShield365.js). getTier(score) → { min, level, name, color, desc };
//    TIER_NAMES_ASC is low→high for the trend-band caption. ──

// Workload-bar color mirrors catColor() in SecureShield365.js.
const barColor = (pct) => (pct >= 80 ? C.green : pct >= 65 ? C.yellow : C.red);

// ── Severity helpers ──
// Severity ordering/ranking/colors now come from ./severity (canonical, shared
// with the dashboard + Excel export). Local-only: the headline breakdown keys,
// which intentionally exclude "informational" from the top-of-report tally.
const BREAKDOWN_KEYS = ["critical", "high", "medium", "low"];

// ── Formatters ──
// Scan executed_at/scored_at are INSTANTS — local rendering is correct.
// Shared formatter from utils/dates.js (same INSTANT vs DATE-ONLY
// classification as the dashboard).
const fmtFullDate = (iso) => fmtInstant(iso, { month: "long", day: "numeric", year: "numeric" });
const fmtShortDate = (iso) => fmtInstant(iso, { month: "short", year: "2-digit" });
const fmtScore = (v) => (typeof v === "number" ? `${Math.round(v)}%` : "—");
const fmtDelta = (v) => {
  if (v == null || isNaN(v)) return "—";
  const r = Math.round(v);
  const sign = r > 0 ? "+" : "";
  return `${sign}${r} pts`;
};
const truncate = (s, n) => {
  if (!s) return "";
  const t = String(s).trim();
  if (t.length <= n) return t;
  return t.slice(0, n - 1).trimEnd() + "…";
};

// ── scored_settings derivations ──
const computeFailed = (scoredSettings) =>
  scoredSettings.filter((s) => s.result === "Failed");

const computeWorkloadCoverage = (scoredSettings) => {
  const map = new Map();
  for (const s of scoredSettings) {
    const w = (s.workload || "Unspecified").toString();
    const entry = map.get(w) || { workload: w, passed: 0, failed: 0, other: 0 };
    if (s.result === "Passed") entry.passed++;
    else if (s.result === "Failed") entry.failed++;
    else entry.other++;
    map.set(w, entry);
  }
  return Array.from(map.values())
    .map((e) => {
      const denom = e.passed + e.failed;
      const pct = denom > 0 ? Math.round((100 * e.passed) / denom) : 0;
      const total = e.passed + e.failed + e.other;
      return { ...e, denom, pct, total };
    })
    .filter((e) => e.denom > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, 8);
};

// ADR-0083 weight gate: "regressed" means the score got worse. Zero-weight
// rows keep their honest regressed_from_passed flag in the report data but
// never appear in What Changed. Same rule as the dashboard's rec.regressed
// gate in SecureShield365.js and the FindingsExport Regressed column.
// ADR-0084 provenance gate: What Changed lists regressions only when the
// report envelope's collection_method === "securescan365" — absent
// (pre-ADR-0084 snapshot) or anything else fails closed. The section then
// says the comparison is unavailable rather than claiming a verified zero.
const computeRegressed = (scoredSettings, collectionMethod) =>
  collectionMethod === "securescan365"
    ? scoredSettings.filter(
        (s) =>
          s.regressed_from_passed === true &&
          (typeof s.max_possible === "number" ? s.max_possible : 0) > 0
      )
    : [];

// ── Disposition helpers ──
// settingDetails is keyed by setting_id (from report.overrides); each value
// carries { override, ... }. Which bucket a row lands in is decided by the
// shared tileDisposition classifier, not restated here.
const dispositionOf = (setting, settingDetails) =>
  settingDetails?.[setting.setting_id]?.override || null;

// A row's bucket for BOTH report sections. Sharing one call is the point:
// the severity tiles and the findings list beneath them are the same
// document, and a reader who adds up the tiles must land on the list.
const bucketOf = (s, settingDetails) =>
  tileDisposition(verdictOf(s), dispositionOf(s, settingDetails));

// Per-severity split of open findings into OPEN (still needs action) vs
// MANAGED/ACCEPTED (credited via override). Shares the dashboard tiles'
// classifier outright (tileDisposition) rather than mirroring it in prose —
// the two surfaces disagreeing is exactly what this classifier exists to
// prevent. A never-assessed row drops out of both counts: `failed` is
// result === "Failed", which already excludes Skipped/Error/NotRun but NOT
// a Failed row whose scored_status is not_applicable / evaluation_failed —
// verdictOf resolves those to not_assessed, and they earn nothing.
export const computeSeveritySplit = (failed, settingDetails) => {
  const out = {
    critical: { open: 0, managed: 0 },
    high:     { open: 0, managed: 0 },
    medium:   { open: 0, managed: 0 },
    low:      { open: 0, managed: 0 },
  };
  for (const s of failed) {
    const k = sevKey(s.severity);
    if (!(k in out)) continue;
    const bucket = bucketOf(s, settingDetails);
    if (bucket === TILE_EXCLUDED) continue;
    if (bucket === TILE_ADDRESSED) out[k].managed++;
    else out[k].open++;
  }
  return out;
};

// Segment failing findings by disposition for the findings section.
//   actionRequired — Critical & High, still needing action (planned stays here).
//   acknowledged   — any severity credited via a credit-granting override.
//   otherOpenCount — still-needs-action Medium & Low remainder, for the
//                    "Plus N additional…" footer.
// Never-assessed rows land in NONE of the three, exactly as they land in
// neither severity-tile bucket above — a Failed row whose scored_status is
// not_applicable / evaluation_failed was never really evaluated, and listing
// it as Action Required while the tile above it doesn't count it makes the
// report disagree with itself.
// Both lists ordered via the shared compareFindings (severity, then weight).
export const computeSegmentedFindings = (failed, settingDetails) => {
  const actionRequired = [];
  const acknowledged = [];
  let otherOpenCount = 0;
  for (const s of failed) {
    const bucket = bucketOf(s, settingDetails);
    if (bucket === TILE_EXCLUDED) continue;
    if (bucket === TILE_ADDRESSED) {
      acknowledged.push({ ...s, _disposition: dispositionOf(s, settingDetails) });
      continue;
    }
    // Not credited → still a true open gap.
    const k = sevKey(s.severity);
    if (k === "critical" || k === "high") actionRequired.push(s);
    else otherOpenCount++;
  }
  actionRequired.sort(compareFindings);
  acknowledged.sort(compareFindings);
  return { actionRequired, acknowledged, otherOpenCount };
};

// ── Executive-summary prose ──
const buildExecutiveSummary = ({ customerName, currentScore, baselineScore, change, scanCount, tierName, verifiedScore, adjustedScore }) => {
  // Round prose values so they match the rounded score tiles. Branch on the
  // rounded change so an "improvement of 0 points" can never appear.
  const rChange = Math.round(change);
  const movement = scanCount <= 1
    ? "This report reflects the first completed scan; baseline-to-current movement will populate on subsequent assessments."
    : rChange > 0
      ? `That represents an improvement of ${rChange} points from the baseline of ${Math.round(baselineScore)}%.`
      : rChange < 0
        ? `That represents a decline of ${Math.abs(rChange)} points from the baseline of ${Math.round(baselineScore)}%.`
        : `The score is unchanged from the baseline of ${Math.round(baselineScore)}%.`;

  // Risk-Adjusted framing — verified vs effective posture, crediting documented
  // dispositions. ss = verified posture, adj = effective posture (with credit).
  const ss = Math.round(verifiedScore);
  const adj = Math.round(adjustedScore);
  const adjusted = `Your verified posture is ${ss}%. Crediting your documented risk acceptances and customer-managed solutions brings effective posture to ${adj}%. The Action Required list below reflects the true open gap.`;

  return `${customerName}'s Patriot Score currently sits at ${Math.round(currentScore)}%, placing the tenant in the ${tierName} tier. ${movement} ${adjusted}`;
};

// ── Trend chart (react-pdf SVG primitives) ──
const TrendChart = ({ scans, adjustedScore, publication }) => {
  if (!scans || scans.length === 0) {
    return (
      <Text style={{ fontSize: 9, color: C.mutedDark, fontStyle: "italic" }}>
        Trend chart will populate after the first completed scan.
      </Text>
    );
  }
  const ordered = scans.slice().reverse();
  const VB_W = 500;
  const VB_H = 200;
  const PAD_L = 30, PAD_R = 14, PAD_T = 10, PAD_B = 24;
  const plotW = VB_W - PAD_L - PAD_R;
  const plotH = VB_H - PAD_T - PAD_B;
  const yFor = (s) => PAD_T + plotH * (1 - s / 100);
  const xFor = (i, n) => (n === 1 ? PAD_L + plotW / 2 : PAD_L + (i / (n - 1)) * plotW);
  const bands = [
    { lo: 90, hi: 100, fill: "rgba(83,196,109,0.20)" },
    { lo: 80, hi: 90,  fill: "rgba(83,196,109,0.10)" },
    { lo: 70, hi: 80,  fill: "rgba(243,181,63,0.14)" },
    { lo: 55, hi: 70,  fill: "rgba(255,122,69,0.13)" },
    { lo: 0,  hi: 55,  fill: "rgba(225,77,87,0.10)" },
  ];
  const points = ordered.map((s, i) => {
    const score = typeof s.score_pct === "number" ? s.score_pct : null;
    if (score == null) return null;
    return {
      x: xFor(i, ordered.length),
      y: yFor(score),
      score,
      scan: s,
      label: fmtShortDate(s.executed_at || s.scored_at),
    };
  }).filter(Boolean);
  const linePath = points.length
    ? points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(" ")
    : "";
  const maxLabels = 8;
  const labelStep = Math.max(1, Math.ceil(points.length / maxLabels));
  const showLabel = (i) => i === 0 || i === points.length - 1 || i % labelStep === 0;
  const latest = points[points.length - 1];
  const latestScan = ordered[ordered.length - 1];

  // Dispositions belong to the selected report, not chronological newest.
  // Without complete exact selection proof there is no adjusted marker.
  const selectedPoint = publication?.selector && publication?.identity?.scored_path
    && /^[a-f0-9]{64}$/.test(publication?.identity?.sha256 || "")
    ? points.find(point => point.scan.scan_id === publication.selector
      && point.scan.identity?.scored_path === publication.identity.scored_path
      && point.scan.identity?.sha256 === publication.identity.sha256) : null;
  const adjMarker = (selectedPoint && typeof adjustedScore === "number" && adjustedScore > selectedPoint.score)
    ? { x: selectedPoint.x, y: yFor(adjustedScore), baseY: selectedPoint.y, score: adjustedScore }
    : null;

  return (
    <View>
      <Svg viewBox={`0 0 ${VB_W} ${VB_H}`} style={{ width: "100%", height: 200 }}>
        <G>
          {bands.map((b, i) => {
            const top = yFor(b.hi);
            const bottom = yFor(b.lo);
            return <Rect key={i} x={PAD_L} y={top} width={plotW} height={bottom - top} fill={b.fill} />;
          })}
          <Line x1={PAD_L} y1={PAD_T} x2={PAD_L} y2={PAD_T + plotH} stroke="rgba(11,31,58,0.45)" strokeWidth={0.5} />
          <Line x1={PAD_L} y1={PAD_T + plotH} x2={PAD_L + plotW} y2={PAD_T + plotH} stroke="rgba(11,31,58,0.45)" strokeWidth={0.5} />
          <Line x1={PAD_L} y1={yFor(50)} x2={PAD_L + plotW} y2={yFor(50)} stroke="rgba(11,31,58,0.12)" strokeWidth={0.4} />
          {linePath !== "" && <Path d={linePath} stroke={C.blue} strokeWidth={1.6} fill="none" />}
          {points.map((p, i) => (
            <Rect key={`pt-${i}`} x={p.x - 2.4} y={p.y - 2.4} width={4.8} height={4.8} fill={C.blue} />
          ))}
          <Text x={PAD_L - 4} y={yFor(100) + 3} style={{ fontSize: 7, fill: C.mutedDark, textAnchor: "end" }}>100</Text>
          <Text x={PAD_L - 4} y={yFor(50) + 3}  style={{ fontSize: 7, fill: C.mutedDark, textAnchor: "end" }}>50</Text>
          <Text x={PAD_L - 4} y={yFor(0) + 3}   style={{ fontSize: 7, fill: C.mutedDark, textAnchor: "end" }}>0</Text>
          {points.map((p, i) => showLabel(i) ? (
            <Text key={`xl-${i}`} x={p.x} y={PAD_T + plotH + 12} style={{ fontSize: 7, fill: C.mutedDark, textAnchor: "middle" }}>{p.label}</Text>
          ) : null)}
          {latest && (
            <Text x={Math.min(latest.x, PAD_L + plotW - 18)} y={Math.max(latest.y - 6, PAD_T + 8)} style={{ fontSize: 8, fill: C.textDark, fontWeight: 700, textAnchor: "middle" }}>
              {fmtScore(latest.score)}
            </Text>
          )}
          {/* Risk-Adjusted marker at the exact selected scan — green to match the
              "verified" composition color; dotted tick connects it to the line. */}
          {adjMarker && (
            <>
              <Line x1={adjMarker.x} y1={adjMarker.baseY} x2={adjMarker.x} y2={adjMarker.y} stroke={OVERRIDE_SLICE_COLORS.verified} strokeWidth={0.6} strokeDasharray="1.5 1.5" />
              <Rect x={adjMarker.x - 2.4} y={adjMarker.y - 2.4} width={4.8} height={4.8} fill={OVERRIDE_SLICE_COLORS.verified} />
              <Text x={Math.min(adjMarker.x, PAD_L + plotW - 18)} y={Math.max(adjMarker.y - 6, PAD_T + 8)} style={{ fontSize: 8, fill: OVERRIDE_SLICE_COLORS.verified, fontWeight: 700, textAnchor: "middle" }}>
                {fmtScore(adjMarker.score)}
              </Text>
            </>
          )}
        </G>
      </Svg>
      <Text style={{ fontSize: 8, color: C.mutedDark, marginTop: 4 }}>
        Latest scan: {fmtFullDate(latestScan.executed_at || latestScan.scored_at)}
        {latest ? ` — verified ${fmtScore(latest.score)}` : ""}
        {adjMarker && selectedPoint === latest ? `, risk-adjusted ${fmtScore(adjMarker.score)}` : ""}. Background bands show maturity tiers ({TIER_NAMES_ASC.join(" / ")}).
      </Text>
    </View>
  );
};

// ── Headline tile (Current Score / Change / Compensating Controls) ──
const Tile = ({ label, value, sub, accent }) => (
  <View style={styles.tile}>
    <View style={[styles.tileAccent, { backgroundColor: accent || C.blue }]} />
    <View style={styles.tileBody}>
      <Text style={styles.tileLabel}>{label}</Text>
      <Text style={styles.tileValue}>{value}</Text>
      {sub ? <Text style={styles.tileSub}>{sub}</Text> : null}
    </View>
  </View>
);

// ── Workload coverage bar list ──
const WorkloadCoverage = ({ workloads }) => {
  if (!workloads || workloads.length === 0) {
    return (
      <Text style={{ fontSize: 9, color: C.mutedDark, fontStyle: "italic" }}>
        No workload data available.
      </Text>
    );
  }
  return (
    <View>
      {workloads.map((w) => {
        const color = barColor(w.pct);
        return (
          <View key={w.workload} style={styles.barRow} wrap={false}>
            <Text style={styles.barLabel}>{truncate(w.workload, 26)}</Text>
            <View style={styles.barTrack}>
              <View style={[styles.barFill, { width: `${w.pct}%`, backgroundColor: color }]} />
            </View>
            <Text style={styles.barValue}>
              <Text>{w.pct}% </Text>
              <Text style={styles.barValueMuted}>({w.passed} of {w.denom})</Text>
            </Text>
          </View>
        );
      })}
    </View>
  );
};

// ── What Changed This Scan ──
const WhatChanged = ({ regressed }) => {
  if (regressed === null) {
    return (
      <View style={styles.changeOk} wrap={false}>
        <Text style={styles.changeOkText}>Regression comparison unavailable for this report.</Text>
      </View>
    );
  }
  if (regressed.length === 0) {
    return (
      <View style={styles.changeOk} wrap={false}>
        <Text style={styles.changeOkText}>No settings regressed against the scoring predecessor.</Text>
      </View>
    );
  }
  return (
    <View style={styles.changeAlert} wrap={false}>
      <Text style={styles.changeAlertHeader}>
        {regressed.length} setting{regressed.length === 1 ? "" : "s"} regressed against the scoring predecessor
      </Text>
      {regressed.map((s) => (
        <Text key={s.setting_id} style={styles.changeAlertItem}>• {s.setting_name
          ? (s.setting_id != null ? `#${s.setting_id} · ${s.setting_name}` : s.setting_name)
          : (s.setting_id != null ? `Setting #${s.setting_id}` : "Untitled setting")}</Text>
      ))}
    </View>
  );
};

// ── Severity pill (used inside finding cards) ──
const SevPill = ({ severity }) => {
  const pal = sevPalette(severity);
  return (
    <View style={[styles.sevPill, { backgroundColor: pal.bg }]}>
      <Text style={[styles.sevPillText, { color: pal.text }]}>{pal.label}</Text>
    </View>
  );
};

// ── Disposition pill (used in the Acknowledged & Managed list) ──
// Colored from OVERRIDE_SLICE_COLORS, labeled with the live COMPOSITION_LABELS
// so the PDF reads identically to the dashboard.
const DispositionPill = ({ disposition }) => {
  const color = OVERRIDE_SLICE_COLORS[disposition] || C.mutedDark;
  const label = COMPOSITION_LABELS[disposition] || disposition || "—";
  return (
    <View style={[styles.dispPill, { borderColor: color }]}>
      <Text style={[styles.dispPillText, { color }]}>{label}</Text>
    </View>
  );
};

// ── Risk-Adjusted hero card (sits beside the SecureShield score in the tiles
//    row). Big adjusted %, a horizontal composition bar, and a legend using the
//    portal's live labels + OVERRIDE_SLICE_COLORS. ──
const RISK_BUCKET_ORDER = ["verified", "risk_accepted", "customer_managed_solution", "customer_remediated", "unresolved_gap"];
export const RiskAdjustedCard = ({ composition }) => {
  const slices = Array.isArray(composition?.riskAdjustedComposition) ? composition.riskAdjustedComposition : [];
  const adj = typeof composition?.adj === "number" ? composition.adj : null;
  const total = slices.reduce((sum, s) => sum + (s.pts || 0), 0);
  // Stable bucket order (matches the dashboard donut/legend order), drop 0-pt
  // buckets from the BAR (keep them out of the fill) but keep all five in the
  // legend so the customer sees what each disposition could affect.
  const ordered = RISK_BUCKET_ORDER
    .map((key) => slices.find((s) => s.key === key))
    .filter(Boolean);
  return (
    <View style={styles.riskCard}>
      <View style={[styles.tileAccent, { backgroundColor: OVERRIDE_SLICE_COLORS.verified }]} />
      <View style={styles.tileBody}>
        <Text style={styles.tileLabel}>Risk-Adjusted Score</Text>
        <Text style={styles.tileValue}>{adj == null ? "—" : `${Math.round(adj)}%`}</Text>
        <Text style={styles.tileSub}>Effective posture with documented credit</Text>
        {/* Horizontal composition bar */}
        <View style={styles.compBar}>
          {total > 0
            ? ordered
                .filter((s) => (s.pts || 0) > 0)
                .map((s) => (
                  <View
                    key={s.key}
                    style={{ width: `${(100 * (s.pts || 0)) / total}%`, backgroundColor: OVERRIDE_SLICE_COLORS[s.key] || C.mutedDark }}
                  />
                ))
            : <View style={{ width: "100%", backgroundColor: "rgba(11,31,58,0.10)" }} />}
        </View>
        {/* Legend — all five buckets, live labels + colors */}
        <View style={styles.compLegend}>
          {ordered.map((s) => {
            const pct = total > 0 ? Math.round((100 * (s.pts || 0)) / total) : 0;
            return (
              <View key={s.key} style={styles.compLegendItem} wrap={false}>
                <View style={[styles.compSwatch, { backgroundColor: OVERRIDE_SLICE_COLORS[s.key] || C.mutedDark }]} />
                <Text style={styles.compLegendLabel}>{s.label}</Text>
                <Text style={styles.compLegendPct}>{pct}%</Text>
              </View>
            );
          })}
        </View>
        {/* F1 — Not Assessed rows carrying a credit override. Same sentence, same
            words, same source helper as the dashboard (composition.js). It is
            NOT a slice and must never be added to RISK_BUCKET_ORDER: a
            pts-bearing slice would credit, and a 0-pt slice is dropped by the
            .filter(Boolean) above. C.mutedDark deliberately, never an
            OVERRIDE_SLICE_COLORS value. Copy approved verbatim by James
            2026-08-20. */}
        {(() => {
          const line = notAssessedOverriddenLine(composition?.notAssessedOverridden);
          if (!line) return null;
          return (
            <Text style={styles.notAssessedOverridden}>
              {line.prefix}
              <Text style={styles.notAssessedOverriddenCounts}>{line.counts}</Text>
              {line.suffix}
            </Text>
          );
        })()}
      </View>
    </View>
  );
};

// ── Severity breakdown with disposition split (Critical / High / Medium / Low).
//    Each cell: large OPEN count + a small muted "+N managed/accepted" footnote.
//    Buckets come from tileDisposition — open (planned counts as open),
//    managed (any credit-granting override), never-assessed rows in neither. ──
const SeveritySplitBreakdown = ({ split }) => (
  <View style={styles.severityRow}>
    {BREAKDOWN_KEYS.map((k) => {
      const pal = sevPalette(k);
      const cell = split?.[k] || { open: 0, managed: 0 };
      return (
        <View key={k} style={[styles.severityCell, { backgroundColor: pal.bg }]} wrap={false}>
          <Text style={[styles.severityCount, { color: pal.text }]}>{cell.open || 0}</Text>
          <Text style={[styles.severityLabel, { color: pal.text }]}>{pal.label}</Text>
          {cell.managed > 0 ? (
            <Text style={[styles.severityFootnote, { color: pal.text }]}>+{cell.managed} managed/accepted</Text>
          ) : null}
        </View>
      );
    })}
  </View>
);

// ── Compact one-line row for Acknowledged & Managed findings (NOT a full card). ──
const AcknowledgedRow = ({ finding }) => {
  const titleText = finding.setting_name
    ? (finding.setting_id != null ? `#${finding.setting_id} · ${finding.setting_name}` : finding.setting_name)
    : (finding.setting_id != null ? `Setting #${finding.setting_id}` : "Untitled setting");
  const wasLabel = sevPalette(finding.severity).label; // "Critical" / "High" / …
  return (
    <View style={styles.ackRow} wrap={false}>
      <Text style={styles.ackTitle}>{truncate(titleText, 64)}</Text>
      <DispositionPill disposition={finding._disposition} />
      <Text style={styles.ackWas}>· was {wasLabel}</Text>
    </View>
  );
};

// ── Top-priority finding card (Critical / High) ──
const FindingCard = ({ finding }) => {
  const recommended = (finding.recommended_value && String(finding.recommended_value).trim())
    || (finding.audit_recommendation && String(finding.audit_recommendation).trim())
    || "";
  const remediationPath = Array.isArray(finding.remediation_steps) && finding.remediation_steps.length > 0
    ? finding.remediation_steps.join(" > ")
    : "";
  const desc = truncate(finding.patriot_advisor_value || "", 220);
  const accent = sevPalette(finding.severity).bg;
  // Prefix the setting number when a name exists; avoid "#1037 · Setting #1037" when it doesn't.
  const titleText = finding.setting_name
    ? (finding.setting_id != null ? `#${finding.setting_id} · ${finding.setting_name}` : finding.setting_name)
    : (finding.setting_id != null ? `Setting #${finding.setting_id}` : "Untitled setting");

  return (
    <View style={[styles.findingCard, { borderLeftColor: accent }]} wrap={false}>
      <View style={styles.findingHeader}>
        <Text style={styles.findingTitle}>{titleText}</Text>
        <SevPill severity={finding.severity} />
      </View>
      {desc ? <Text style={styles.findingDesc}>{desc}</Text> : null}
      {recommended ? (
        <Text style={styles.findingMeta}>
          <Text style={styles.findingMetaLabel}>Recommended: </Text>
          <Text>{recommended}</Text>
        </Text>
      ) : null}
      {remediationPath ? (
        <Text style={styles.findingMeta}>
          <Text style={styles.findingMetaLabel}>Where: </Text>
          <Text>{remediationPath}</Text>
        </Text>
      ) : null}
    </View>
  );
};

// ── Footer ──
const PageFooter = ({ customerName }) => (
  <View style={styles.footer} fixed>
    <Text style={styles.footerText}>Patriot Consulting — SecureShield365 Executive Report</Text>
    <Text style={styles.footerText}>{customerName} · Confidential</Text>
  </View>
);

const styles = StyleSheet.create({
  page: {
    backgroundColor: C.paper,
    paddingTop: 40,
    paddingBottom: 50,
    paddingHorizontal: 40,
    fontSize: 10,
    color: C.textDark,
    fontFamily: "Helvetica",
  },
  // ── Cover ──
  headerBand: {
    backgroundColor: C.bg,
    paddingHorizontal: 22,
    paddingVertical: 22,
    borderRadius: 6,
    marginBottom: 18,
  },
  brandLockup: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  patriotLogo: {
    width: 100,
    height: 36,
    objectFit: "contain",
  },
  customerLogo: {
    width: 120,
    height: 36,
    objectFit: "contain",
  },
  brandFallbackText: {
    fontSize: 12,
    color: C.textLight,
    fontWeight: 700,
    maxWidth: 240,
  },
  brandFallbackTextRight: {
    fontSize: 12,
    color: C.textLight,
    fontWeight: 700,
    textAlign: "right",
    maxWidth: 240,
  },
  eyebrow: {
    fontSize: 8,
    color: C.mutedLight,
    textTransform: "uppercase",
    letterSpacing: 1.2,
    marginBottom: 6,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: C.textLight,
    marginBottom: 4,
  },
  rule: {
    height: 2,
    width: 72,
    backgroundColor: C.blue,
    marginTop: 6,
    marginBottom: 12,
  },
  customer: {
    fontSize: 15,
    color: C.textLight,
    marginBottom: 6,
  },
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 6,
  },
  metaItem: {
    fontSize: 9,
    color: C.mutedLight,
    marginRight: 18,
  },
  // ── Section chrome ──
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: C.textDark,
    textTransform: "uppercase",
    letterSpacing: 0.6,
    marginTop: 16,
    marginBottom: 8,
  },
  prose: {
    fontSize: 10,
    color: C.textDark,
    lineHeight: 1.55,
  },
  // ── Headline tiles ──
  tilesRow: {
    flexDirection: "row",
    marginTop: 12,
    marginBottom: 6,
  },
  tile: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: C.paperAlt,
    borderRadius: 6,
    marginRight: 8,
    overflow: "hidden",
  },
  tileAccent: { width: 3 },
  tileBody: { flex: 1, padding: 10 },
  tileLabel: {
    fontSize: 8,
    color: C.mutedDark,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  tileValue: {
    fontSize: 18,
    fontWeight: 700,
    color: C.textDark,
    marginBottom: 2,
  },
  tileSub: { fontSize: 8, color: C.mutedDark },
  // ── Severity breakdown ──
  severityRow: {
    flexDirection: "row",
    marginTop: 8,
    marginBottom: 6,
  },
  severityCell: {
    flex: 1,
    paddingVertical: 10,
    paddingHorizontal: 10,
    marginRight: 6,
    borderRadius: 6,
  },
  severityCount: {
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1.1,
  },
  severityLabel: {
    fontSize: 9,
    marginTop: 4,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    fontWeight: 700,
  },
  severityFootnote: {
    fontSize: 7,
    marginTop: 3,
    opacity: 0.85,
  },
  // ── Risk-Adjusted hero card (tiles row) ──
  riskCard: {
    flex: 1,
    flexDirection: "row",
    backgroundColor: C.paperAlt,
    borderRadius: 6,
    overflow: "hidden",
  },
  compBar: {
    flexDirection: "row",
    height: 8,
    borderRadius: 4,
    overflow: "hidden",
    marginTop: 8,
    marginBottom: 6,
  },
  compLegend: {
    marginTop: 2,
  },
  compLegendItem: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 2,
  },
  compSwatch: {
    width: 6,
    height: 6,
    borderRadius: 1,
    marginRight: 5,
  },
  compLegendLabel: {
    flex: 1,
    fontSize: 7.5,
    color: C.textDark,
  },
  compLegendPct: {
    fontSize: 7.5,
    color: C.mutedDark,
    fontWeight: 700,
  },
  // F1 count line — muted, no swatch, no pill, no credit colour.
  notAssessedOverridden: {
    fontSize: 7,
    color: C.mutedDark,
    marginTop: 5,
  },
  notAssessedOverriddenCounts: {
    fontWeight: 700,
  },
  // ── Disposition pill (Acknowledged & Managed rows) ──
  dispPill: {
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 999,
    borderWidth: 0.75,
    marginHorizontal: 6,
  },
  dispPillText: {
    fontSize: 7.5,
    fontWeight: 700,
  },
  // ── Acknowledged & Managed compact rows ──
  ackSubhead: {
    fontSize: 9,
    color: C.mutedDark,
    marginTop: 2,
    marginBottom: 6,
  },
  ackRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: "rgba(11,31,58,0.08)",
  },
  ackTitle: {
    fontSize: 9,
    color: C.mutedDark,
    flexShrink: 1,
  },
  ackWas: {
    fontSize: 8,
    color: C.mutedDark,
    fontStyle: "italic",
  },
  // ── Workload bars ──
  barRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 6,
  },
  barLabel: {
    width: 140,
    fontSize: 9,
    color: C.textDark,
    paddingRight: 8,
  },
  barTrack: {
    flex: 1,
    height: 8,
    backgroundColor: "rgba(11,31,58,0.08)",
    borderRadius: 4,
    overflow: "hidden",
  },
  barFill: {
    height: 8,
    borderRadius: 4,
  },
  barValue: {
    width: 100,
    fontSize: 9,
    color: C.textDark,
    paddingLeft: 8,
    textAlign: "right",
    fontWeight: 700,
  },
  barValueMuted: {
    fontWeight: 400,
    color: C.mutedDark,
  },
  // ── What Changed ──
  changeOk: {
    marginTop: 8,
    padding: 10,
    backgroundColor: "rgba(83,196,109,0.10)",
    borderLeftWidth: 3,
    borderLeftColor: C.green,
    borderRadius: 4,
  },
  changeOkText: {
    fontSize: 10,
    color: C.textDark,
    fontWeight: 600,
  },
  changeAlert: {
    marginTop: 8,
    padding: 10,
    backgroundColor: "rgba(225,77,87,0.10)",
    borderLeftWidth: 3,
    borderLeftColor: C.red,
    borderRadius: 4,
  },
  changeAlertHeader: {
    fontSize: 10,
    color: C.textDark,
    fontWeight: 700,
    marginBottom: 4,
  },
  changeAlertItem: {
    fontSize: 9,
    color: C.textDark,
    marginTop: 2,
  },
  // ── Finding cards ──
  findingCard: {
    marginBottom: 9,
    padding: 10,
    backgroundColor: C.paperAlt,
    borderLeftWidth: 3,
    borderLeftColor: C.critical,
    borderRadius: 4,
  },
  findingHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 5,
  },
  findingTitle: {
    flex: 1,
    fontSize: 11,
    fontWeight: 700,
    color: C.textDark,
    marginRight: 8,
  },
  findingDesc: {
    fontSize: 9,
    color: C.textDark,
    lineHeight: 1.5,
    marginBottom: 4,
  },
  findingMeta: {
    fontSize: 8.5,
    color: C.mutedDark,
    lineHeight: 1.45,
    marginTop: 2,
  },
  findingMetaLabel: {
    fontWeight: 700,
    color: C.textDark,
  },
  sevPill: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  },
  sevPillText: {
    fontSize: 8,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  findingsFooter: {
    fontSize: 9,
    color: C.mutedDark,
    fontStyle: "italic",
    marginTop: 6,
  },
  // ── Scan history table ──
  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: C.paperAlt,
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(11,31,58,0.12)",
  },
  tableHeaderCell: {
    fontSize: 8,
    fontWeight: 700,
    color: C.mutedDark,
    textTransform: "uppercase",
    letterSpacing: 0.4,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderBottomWidth: 0.5,
    borderBottomColor: "rgba(11,31,58,0.08)",
  },
  tableCell: {
    fontSize: 9,
    color: C.textDark,
  },
  // ── Footer ──
  footer: {
    position: "absolute",
    bottom: 22,
    left: 40,
    right: 40,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  footerText: {
    fontSize: 7,
    color: C.mutedDark,
  },
});

// ── Document ──
export const ReportDocument = ({ customerName, slug, report, scans, settingDetails, composition, patriotLogoSrc, customerLogoSrc }) => {
  const safeScans = Array.isArray(scans) ? scans : [];
  const totals = report?.totals?.score_pct;
  if (typeof totals !== "number") {
    return <Document><Page size="A4"><Text>{customerName || report?.customer_name || "Client"}</Text></Page></Document>;
  }
  const currentScore = typeof totals === "number"
    ? totals
    : 0;
  const baselineScan = exactScoringBaseline(report, safeScans);
  const baselineScore = typeof baselineScan?.score_pct === "number" ? baselineScan.score_pct : currentScore;
  const change = currentScore - baselineScore;
  // Count ONLY credit-granting overrides (risk_accepted / customer_managed_solution
  // / customer_remediated) — same CREDIT_OVERRIDES rule that drives the composition
  // bar, the "+N managed/accepted" severity footnotes, and the Acknowledged &
  // Managed section. planned (and any non-crediting state) is excluded so this tile
  // reconciles with the rest of the disposition-aware report. Source is settingDetails
  // (the disposition prop), falling back to report.overrides when it's absent.
  const dispositionMap = settingDetails || report?.overrides || {};
  const creditOverrideCount = Object.values(dispositionMap)
    .filter((v) => CREDIT_OVERRIDES.includes(v?.override)).length;
  const tier = getTier(currentScore);
  const displayName = customerName || report?.customer_name || "Client";

  const newestScan = safeScans.find(scan => scan.scan_id === report?._publication?.selector);
  const newestIso = newestScan?.executed_at || newestScan?.scored_at || report?.executed_at || report?.scored_at;
  const oldestIso = baselineScan?.executed_at || baselineScan?.scored_at;
  const reportingPeriod = (() => {
    if (!newestIso && !oldestIso) return fmtFullDate(report?.executed_at || report?.scored_at);
    if (safeScans.length <= 1 || !oldestIso || oldestIso === newestIso) return fmtFullDate(newestIso);
    return `${fmtFullDate(oldestIso)} — ${fmtFullDate(newestIso)}`;
  })();

  // Risk-Adjusted score: verified posture (ss) is the canonical Patriot Score;
  // effective posture (adj) comes from the shared composition (computeComposition)
  // passed in by the dashboard so the PDF can't drift from the donut. Falls back
  // to the verified score when composition is unavailable (older caller).
  const verifiedScore = currentScore;
  const adjustedScore = typeof composition?.adj === "number" ? composition.adj : verifiedScore;

  const summaryProse = buildExecutiveSummary({
    customerName: displayName,
    currentScore,
    baselineScore,
    change,
    scanCount: baselineScan ? safeScans.length : 1,
    tierName: tier.name,
    verifiedScore,
    adjustedScore,
  });

  // scored_settings derivations — graceful skip when absent.
  const scoredSettings = Array.isArray(report?.scored_settings) ? report.scored_settings : null;
  const hasFindings = !!scoredSettings;
  const failed = hasFindings ? computeFailed(scoredSettings) : [];
  const workloads = hasFindings ? computeWorkloadCoverage(scoredSettings) : [];
  // Regressions only when the prior scan actually still exists in history:
  // regressed_from_passed is a scoring-time snapshot and survives deletion
  // of the prior scan (83-customer ghost audit, July 6 2026) — a single-scan
  // report must not assert regressions vs a scan we deliberately retired.
  const hasScanHistory = safeScans.length >= 2;
  // ADR-0084 amendment (2026-08-08): the baseline must be SecureScan365 too —
  // pre-deploy blobs carry flags computed against Excel-import baselines.
  // Join via previous_scored_at -> scans[].scored_at; fail closed on no match.
  const regressed = hasFindings && hasScanHistory
      && baselineIsSecureScan365(report, safeScans)
    ? computeRegressed(scoredSettings, report?.collection_method)
    : null;

  // Disposition-aware derivations (settingDetails keyed by setting_id).
  // severitySplit  — per-severity OPEN vs MANAGED/ACCEPTED for the tiles.
  // actionRequired — Critical & High, true-open (no credit override; planned=open).
  // acknowledged   — credited via override (risk_accepted / customer_managed_solution
  //                  / customer_remediated), shown as compact muted rows.
  // otherOpenCount — true-open Medium & Low remainder for the "Plus N…" footer.
  const severitySplit = hasFindings ? computeSeveritySplit(failed, settingDetails) : null;
  const { actionRequired, acknowledged, otherOpenCount } = hasFindings
    ? computeSegmentedFindings(failed, settingDetails)
    : { actionRequired: [], acknowledged: [], otherOpenCount: 0 };

  // Per-scan delta table (scans newest-first; delta vs the next-older entry).
  // Deltas derive from ROUNDED scores so the Change column reconciles with the
  // rounded score tiles / headline (avoids a column that sums off-by-one).
  const tableRows = safeScans.map((s, i) => {
    const isSelected = s.scan_id === report?._publication?.selector;
    const prior = isSelected ? baselineScan : null;
    const score = typeof s.score_pct === "number" ? s.score_pct : null;
    const priorScore = typeof prior?.score_pct === "number" ? prior.score_pct : null;
    const rScore = score != null ? Math.round(score) : null;
    const rPrior = priorScore != null ? Math.round(priorScore) : null;
    const delta = (rScore != null && rPrior != null) ? rScore - rPrior : null;
    // Risk-Adjusted Score belongs to the selected report using current-state
    // overrides, not historical override snapshots. Show it only on that
    // selected row; other rows show "—" rather than implying a stored value.
    const adjusted = (isSelected && typeof adjustedScore === "number") ? adjustedScore : null;
    return {
      date: fmtFullDate(s.executed_at || s.scored_at),
      score,
      adjusted,
      delta,
      tierName: score != null ? getTier(score).name : "—",
    };
  });

  return (
    <Document
      title={`${displayName} — SecureShield365 Executive Report`}
      author="Patriot Consulting"
      subject="SecureShield365 Executive Report"
    >
      {/* ── Page 1 — cover + executive summary + tiles + severity + trend ── */}
      <Page size="LETTER" style={styles.page}>
        <View style={styles.headerBand}>
          <View style={styles.brandLockup}>
            {patriotLogoSrc
              ? <Image src={patriotLogoSrc} style={styles.patriotLogo} />
              : <Text style={styles.brandFallbackText}>Patriot Consulting</Text>}
            {customerLogoSrc
              ? <Image src={customerLogoSrc} style={styles.customerLogo} />
              : <Text style={styles.brandFallbackTextRight}>{displayName}</Text>}
          </View>
          <Text style={styles.eyebrow}>Patriot Consulting · SecureShield365</Text>
          <Text style={styles.title}>Security Posture Executive Report</Text>
          <View style={styles.rule} />
          <Text style={styles.customer}>{displayName}</Text>
          <View style={styles.metaRow}>
            <Text style={styles.metaItem}>Reporting period: {reportingPeriod}</Text>
            <Text style={styles.metaItem}>Scans included: {safeScans.length}</Text>
            <Text style={styles.metaItem}>Current tier: Level {tier.level} · {tier.name}</Text>
          </View>
        </View>

        <Text style={styles.sectionTitle}>Executive Summary</Text>
        <Text style={styles.prose}>{summaryProse}</Text>

        {/* Score row — verified (SecureShield) score beside the Risk-Adjusted card. */}
        <View style={styles.tilesRow}>
          <Tile
            label="SecureShield Score"
            value={fmtScore(currentScore)}
            sub={`Verified posture · Level ${tier.level} · ${tier.name}`}
            accent={tier.color}
          />
          <RiskAdjustedCard composition={composition} />
        </View>
        <View style={styles.tilesRow}>
          <Tile
            label="Change Since Baseline"
            value={safeScans.length <= 1 ? "—" : fmtDelta(change)}
            sub={`across ${safeScans.length} scan${safeScans.length === 1 ? "" : "s"}`}
            accent={change > 0 ? C.green : change < 0 ? C.red : C.mutedDark}
          />
          <Tile
            label="Compensating Controls"
            value={String(creditOverrideCount)}
            sub="Accepted risk / customer-managed"
            accent={C.blue}
          />
        </View>

        {hasFindings && (
          <>
            <Text style={styles.sectionTitle}>Open Findings by Severity</Text>
            <SeveritySplitBreakdown split={severitySplit} />
          </>
        )}

        <Text style={styles.sectionTitle}>Patriot Score Trend</Text>
        <TrendChart scans={safeScans} adjustedScore={adjustedScore} publication={report?._publication} />

        <PageFooter customerName={displayName} />
      </Page>

      {/* ── Page 2 — workload coverage, what changed, top findings (auto-paginates) ── */}
      {hasFindings && (
        <Page size="LETTER" style={styles.page}>
          <Text style={styles.sectionTitle}>Product Coverage</Text>
          <Text style={styles.prose}>
            Compliance = settings passed ÷ (passed + failed) within each product. Skipped or errored checks are excluded from the denominator. Top {workloads.length} products by setting count shown.
          </Text>
          <View style={{ marginTop: 8 }}>
            <WorkloadCoverage workloads={workloads} />
          </View>

          {/* Suppressed entirely for single-scan customers — even the "no
              regressions" positive statement implies a prior assessment. */}
          {hasScanHistory && (
            <>
              <Text style={styles.sectionTitle}>Regressions vs Scoring Predecessor</Text>
              <WhatChanged regressed={regressed} />
            </>
          )}

          <Text style={styles.sectionTitle}>Action Required (Critical &amp; High)</Text>
          {actionRequired.length === 0 ? (
            <Text style={styles.prose}>
              No Critical or High severity findings require action. Focus shifts to Medium and Low hygiene from here.
            </Text>
          ) : (
            <>
              {actionRequired.map((f) => (
                <FindingCard key={f.setting_id} finding={f} />
              ))}
              {otherOpenCount > 0 && (
                <Text style={styles.findingsFooter}>
                  Plus {otherOpenCount} additional open Medium and Low finding{otherOpenCount === 1 ? "" : "s"} — see the full Recommendations report.
                </Text>
              )}
            </>
          )}

          {acknowledged.length > 0 && (
            <>
              <Text style={styles.sectionTitle}>Acknowledged &amp; Managed — no action required</Text>
              <Text style={styles.ackSubhead}>
                Findings credited in your Risk-Adjusted Score via a documented risk acceptance, customer-managed solution, or customer-remediated record.
              </Text>
              {acknowledged.map((f) => (
                <AcknowledgedRow key={f.setting_id} finding={f} />
              ))}
            </>
          )}

          <PageFooter customerName={displayName} />
        </Page>
      )}

      {/* ── Final page — scan history + risk management posture ── */}
      <Page size="LETTER" style={styles.page}>
        <Text style={styles.sectionTitle}>Scan-by-Scan History</Text>
        <View style={styles.tableHeaderRow}>
          <Text style={[styles.tableHeaderCell, { flex: 2.6 }]}>Scan Date</Text>
          <Text style={[styles.tableHeaderCell, { flex: 1.3, textAlign: "right" }]}>Verified Score</Text>
          <Text style={[styles.tableHeaderCell, { flex: 1.4, textAlign: "right" }]}>Risk-Adjusted Score</Text>
          <Text style={[styles.tableHeaderCell, { flex: 1.2, textAlign: "right" }]}>Change (pts)</Text>
          <Text style={[styles.tableHeaderCell, { flex: 1.8, borderLeftWidth: 0.5, borderLeftColor: "rgba(11,31,58,0.12)", paddingLeft: 10 }]}>Maturity Tier</Text>
        </View>
        {tableRows.length === 0 ? (
          <View style={styles.tableRow}>
            <Text style={[styles.tableCell, { flex: 1 }]}>No completed scans yet.</Text>
          </View>
        ) : (
          tableRows.map((r, i) => (
            <View key={i} style={styles.tableRow} wrap={false}>
              <Text style={[styles.tableCell, { flex: 2.6 }]}>{r.date}</Text>
              <Text style={[styles.tableCell, { flex: 1.3, textAlign: "right" }]}>{fmtScore(r.score)}</Text>
              <Text style={[styles.tableCell, { flex: 1.4, textAlign: "right" }]}>{r.adjusted == null ? "—" : fmtScore(r.adjusted)}</Text>
              <Text style={[styles.tableCell, { flex: 1.2, textAlign: "right" }]}>
                {r.delta == null ? "—" : fmtDelta(r.delta)}
              </Text>
              <Text style={[styles.tableCell, { flex: 1.8, borderLeftWidth: 0.5, borderLeftColor: "rgba(11,31,58,0.12)", paddingLeft: 10 }]}>{r.tierName}</Text>
            </View>
          ))
        )}

        <Text style={styles.sectionTitle}>Risk Management Posture</Text>
        <Text style={styles.prose}>
          {creditOverrideCount === 0
            ? `${displayName} has not yet recorded any compensating controls or accepted-risk overrides. The Patriot Score above reflects raw control alignment with the SecureShield365 hardening baseline; the Risk-Adjusted view will diverge from it as the team documents customer-managed solutions, documented exceptions, or compensating mitigations.`
            : `${displayName} has ${creditOverrideCount} compensating control${creditOverrideCount === 1 ? "" : "s"} or accepted-risk record${creditOverrideCount === 1 ? "" : "s"} on file. These are reflected in the Risk-Adjusted view of the SecureShield365 program. Compensating and customer-managed controls may satisfy the intent of a Patriot recommendation through a different mechanism; accepted risks are documented, acknowledged exposures that remain open by business decision. These records are captured in SecureShield365 and revisited during your scheduled SecureShield365 review sessions.`}
        </Text>

        <PageFooter customerName={displayName} />
      </Page>
    </Document>
  );
};

// ── Download button — pill aesthetic matching the dashboard header pills ──
//
// Render-on-click, deliberately NOT PDFDownloadLink: that component (and
// usePDF) renders the ENTIRE document eagerly on mount and again on every
// document prop change, so every dashboard page load paid for at least two
// full background PDF renders nobody asked for (operator decision
// 2026-08-03). The imperative pdf().toBlob() call below touches no PDF
// machinery until the user clicks. Tradeoff: the download is now
// "click → brief Preparing… → file" instead of instant.
export const DownloadReportButton = ({ customerName, slug, report, scans, settingDetails, composition, T, onProductAnalyticsAction }) => {
  // HEAD-check both logo URLs up front; only pass them to react-pdf when we
  // know the asset exists, since Image throws on a 404 and would crash the
  // entire PDF generation.
  const [patriotLogoSrc, setPatriotLogoSrc] = React.useState(null);
  const [customerLogoSrc, setCustomerLogoSrc] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [renderError, setRenderError] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;
    fetch("/patriot-logo.png", { method: "HEAD" })
      .then((r) => { if (!cancelled && r.ok) setPatriotLogoSrc("/patriot-logo.png"); })
      .catch(() => { /* leave fallback */ });
    return () => { cancelled = true; };
  }, []);

  React.useEffect(() => {
    setCustomerLogoSrc(null);
    if (!slug) return;
    let cancelled = false;
    const url = `/assets/logos/${slug}.png`;
    fetch(url, { method: "HEAD" })
      .then((r) => { if (!cancelled && r.ok) setCustomerLogoSrc(url); })
      .catch(() => { /* leave fallback */ });
    return () => { cancelled = true; };
  }, [slug]);

  const hasData = !!report && Array.isArray(scans) && scans.length > 0;

  // Mirrors the scan-dropdown button styling in SecureShield365.js.
  const baseStyle = {
    fontSize: 11,
    color: T?.muted || "#a8bddc",
    padding: "4px 10px",
    borderRadius: 6,
    border: `1px solid ${T?.border || "rgba(255,255,255,0.08)"}`,
    background: "rgba(255,255,255,0.02)",
    fontFamily: "inherit",
    fontWeight: 600,
    textDecoration: "none",
    display: "inline-block",
    lineHeight: 1,
  };

  if (!hasData) {
    return (
      <span
        style={{ ...baseStyle, opacity: 0.4, cursor: "not-allowed" }}
        title="Report unavailable — waiting on first completed scan"
      >
        Download report
      </span>
    );
  }

  const fileName = `${slug || "client"}-SecureShield365-Executive-Report.pdf`;

  const handleDownload = async () => {
    if (busy) return;
    try { onProductAnalyticsAction?.("executive_report_download_requested"); } catch { /* observation must not alter download */ }
    setBusy(true);
    setRenderError(null);
    try {
      const blob = await pdf(
        <ReportDocument
          customerName={customerName}
          slug={slug}
          report={report}
          scans={scans}
          settingDetails={settingDetails}
          composition={composition}
          patriotLogoSrc={patriotLogoSrc}
          customerLogoSrc={customerLogoSrc}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      // Defer revocation a tick so the click's navigation grabs the blob
      // before the URL dies.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error("Executive report render failed:", e);
      setRenderError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={busy}
      style={{
        ...baseStyle,
        cursor: busy ? "progress" : "pointer",
        opacity: busy ? 0.6 : 1,
      }}
      title={renderError ? `Report failed to render — click to retry (${renderError.message})` : undefined}
    >
      {busy ? "Preparing…" : renderError ? "Report error — retry" : "Download report"}
    </button>
  );
};

export default ReportDocument;
